import IntentLandingLayout from "@/components/landing/IntentLandingLayout";

export default function QualificationAppelsImmobilier() {
  return (
    <IntentLandingLayout
      canonical="/qualification-appels-immobilier"
      title="Qualification des appels immobiliers par IA | Immovox"
      description="Qualification des appels immobiliers : les bonnes questions pour vendeurs, acquéreurs, locataires et propriétaires, avec une synthèse prête pour le rappel."
      eyebrow="Qualification appels immobilier"
      h1="Qualifiez vos appels immobiliers avant de rappeler."
      lede="Vendeur, acquéreur, candidat locataire ou propriétaire : Immovox adapte ses questions au motif de l’appel et transmet uniquement le contexte utile."
      serviceType="Qualification des appels entrants immobiliers"
      primaryCtaLabel="Tester la qualification"
      acquisitionHero={{
        visual: "qualification",
        visualLabel: "Qualification des appels immobiliers",
        visualTitle: "Une conversation devient un contexte exploitable.",
        visualText: "Le scénario s’adapte au profil et au motif de l’appel, sans transformer l’échange en interrogatoire.",
        secondaryCtaLabel: "Voir une qualification",
        strip: ["Identifier le profil et le motif", "Poser les questions propres au scénario", "Préparer l’action suivante de l’agence"],
      }}
      definition={<>La <strong>qualification d'un appel immobilier</strong> consiste à identifier le motif, le profil de l'appelant et les informations utiles au traitement de sa demande. Les questions doivent changer selon qu'il s'agit d'un vendeur, d'un acquéreur, d'un candidat locataire ou d'un propriétaire.</>}
      sections={[
        {
          title: "Qualifier un vendeur sans transformer l'appel en interrogatoire",
          bullets: [
            "Nature du projet : estimation, vente prochaine ou simple prise d'information.",
            "Type de bien et localisation.",
            "Échéance ou maturité du projet lorsqu'elle est connue.",
            "Situation utile au rendez-vous d'estimation, sans demander des informations superflues.",
            "Coordonnées et disponibilité pour le rappel.",
          ],
        },
        {
          title: "Qualifier un acquéreur",
          bullets: [
            "Bien ou annonce à l'origine de l'appel lorsqu'il y en a un.",
            "Secteur et type de bien recherchés.",
            "Budget communiqué par le prospect.",
            "Critères importants pour éviter un rappel sans contexte.",
            "Disponibilités si la prochaine étape est une visite ou un échange avec un agent.",
          ],
        },
        {
          title: "Qualifier une demande locative",
          bullets: [
            "Annonce ou type de logement concerné.",
            "Date ou horizon de recherche.",
            "Critères exprimés par le candidat.",
            "Coordonnées utiles au suivi.",
            "Questions de dossier uniquement lorsqu'elles sont nécessaires au processus défini par l'agence.",
          ],
        },
        {
          title: "Propriétaire, gestion et demandes techniques",
          bullets: [
            "Identifier le bien ou le dossier concerné.",
            "Distinguer une demande commerciale de gestion d'une demande de suivi existante.",
            "Recueillir le problème décrit par l'appelant sans le reformuler comme un diagnostic.",
            "Faire remonter les éléments permettant à l'équipe de juger la priorité.",
          ],
        },
        {
          title: "Qualification, scoring et décision : ne pas tout confondre",
          paragraphs: [
            "La qualification collecte des faits communiqués pendant l'appel. Le scoring classe éventuellement une demande selon des critères définis par l'entreprise. La décision commerciale reste une étape distincte.",
            "Cette séparation est importante pour garder une synthèse fidèle : l'assistant doit restituer ce qui a été dit et les règles appliquées, pas inventer une probabilité de succès.",
          ],
        },
        {
          title: "À quoi ressemble une fiche d'appel exploitable ?",
          bullets: [
            "Profil de l'appelant et motif de la demande.",
            "Coordonnées recueillies.",
            "Bien, zone ou dossier concerné.",
            "Réponses aux questions propres au scénario.",
            "Résumé court de la conversation et prochaine action attendue.",
          ],
        },
      ]}
      comparison={{
        title: "Les informations utiles selon le type d'appel",
        headers: ["Appelant", "À qualifier en priorité", "Suite typique"],
        rows: [
          ["Vendeur", "Bien, localisation, projet, délai", "Rappel ou rendez-vous d'estimation"],
          ["Acquéreur", "Bien, budget, secteur, critères", "Rappel ou visite"],
          ["Candidat locataire", "Annonce, horizon, critères", "Traitement du dossier ou visite"],
          ["Propriétaire bailleur", "Bien, besoin de gestion, délai", "Rappel commercial"],
          ["Client en gestion", "Dossier, motif, contexte", "Orientation vers le bon interlocuteur"],
        ],
      }}
      faq={[
        { question: "Qu'est-ce qu'un prospect immobilier qualifié ?", answer: "C'est un prospect pour lequel l'agence dispose déjà des informations nécessaires pour comprendre la demande et préparer l'action suivante. Cela ne signifie pas qu'une décision commerciale a déjà été prise." },
        { question: "Combien de questions faut-il poser ?", answer: "Le moins possible tout en obtenant les informations nécessaires. Un scénario trop long dégrade l'expérience et collecte souvent des données qui ne seront pas utilisées." },
        { question: "Les questions doivent-elles être les mêmes pour tous les appels ?", answer: "Non. Un vendeur, un acquéreur et un locataire n'ont ni le même besoin ni les mêmes informations pertinentes." },
        { question: "L'IA peut-elle prioriser les appels ?", answer: "Elle peut appliquer des règles explicites définies par l'agence à partir des informations recueillies. Elle ne doit pas inventer de critères ou de faits absents de l'échange." },
        { question: "Où vont les informations après l'appel ?", answer: "Immovox restitue une synthèse structurée afin que l'équipe puisse reprendre la demande avec le contexte déjà collecté." },
      ]}
      related={[
        { to: "/standard-telephonique-immobilier", label: "Standard téléphonique IA", description: "Appliquer la qualification directement aux appels entrants." },
        { to: "/blog/qualifier-un-lead-vendeur-la-methode-des-meilleures-agences-immobilieres", label: "Qualifier un lead vendeur", description: "Les questions utiles pour préparer une estimation sans surcharger l'appel." },
        { to: "/blog/assistant-vocal-ia-immobilier-utilite-agence", label: "Assistant vocal IA immobilier", description: "Comprendre le fonctionnement d'un agent vocal en agence." },
      ]}
      ctaTitle="Construire vos scénarios de qualification"
      ctaText="Testez les questions à poser sur vos vrais motifs d'appel et vérifiez que la synthèse finale est directement exploitable par l'équipe."
    />
  );
}
